import React from 'react';
import { AvButton } from '@fabric-ui/components';
import { Internal, X } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useNavigate } from '@utils/AvRouter';
import { getTableTypeTitle, handleNavigation } from '../utils';
import InteractiveTable from './interactiveTable';
import { FilterContext, InteractiveWidgetType, SelectedWidgetData } from './types';
import AlertsMappedToTactic from './widgets/alertsByTactic';
import IncidentStatus from './widgets/incidentStatus';
import MeanTimeToRespond from './widgets/meanTimeToRespond';
import Top5AttackedEntities from './widgets/top5AttackedEntities';
import Top5VendorsByAlertsVsIncidents from './widgets/top5VendorsByAlertsVsIncidents';

interface InteractiveViewProps {
  filterContext: FilterContext;
  closeInteractive: () => void;
  activeWidget: {
    type: InteractiveWidgetType;
    selected: SelectedWidgetData;
    title: string;
  };
  onSelect: (selected: SelectedWidgetData) => void;
}

const widgetMap = {
  [InteractiveWidgetType.MeanTimeToRespond]: MeanTimeToRespond,
  [InteractiveWidgetType.Top5AttackedEntities]: Top5AttackedEntities,
  [InteractiveWidgetType.Top5Vendors]: Top5VendorsByAlertsVsIncidents,
  [InteractiveWidgetType.AlertsMappedToTactic]: AlertsMappedToTactic,
  [InteractiveWidgetType.IncidentStatus]: IncidentStatus,
};

const InteractiveView = ({ closeInteractive, filterContext, activeWidget, onSelect }: InteractiveViewProps) => {
  const currentFilters = { ...filterContext.filters, ...activeWidget.selected.filter };
  const currentFilterContext = { ...filterContext, filters: currentFilters };
  const { tableType } = activeWidget.selected;
  const tableTypeTitle = getTableTypeTitle(tableType);
  const navigate = useNavigate();
  const WidgetComponent = widgetMap[activeWidget.type];

  return (
    <Box
      sx={{
        ...flex.row,
        backgroundColor: 'var(--color-background-primary)',
        height: '80%',
      }}>
      <Box sx={{ width: '40%', borderRight: `1px solid var(--color-border-base-subdued)`, ...flex.col }}>
        <Box sx={{ height: '350px' }}>
          {WidgetComponent && (
            <WidgetComponent
              filterContext={filterContext}
              closeInteractive={closeInteractive}
              selected={activeWidget.selected}
              onSelect={onSelect}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ width: '60%', padding: '16px', ...flex.col }}>
        <Box sx={{ ...flex.justifyBetweenCenter }}>
          <Typography variant="h5" sx={{ lineHeight: '27px', paddingBottom: '16px', color: 'var(--content-base-primary)' }}>
            {activeWidget.title}
          </Typography>

          <Box sx={{ ...flex.center, gap: '14px' }}>
            <AvButton
              sx={{ gap: '4px' }}
              size="small"
              variant="primaryText"
              onClick={() => handleNavigation(currentFilterContext, tableType, navigate)}>
              Open List in {tableTypeTitle} page
              <Internal />
            </AvButton>
            <Divider flexItem orientation="vertical" />
            <IconButton onClick={closeInteractive}>
              <X style={{ width: 20, height: 20 }} />
            </IconButton>
          </Box>
        </Box>
        <InteractiveTable filterContext={currentFilterContext} tableType={tableType} />
      </Box>
    </Box>
  );
};

export default InteractiveView;
